import * as React from 'react';
import { Checkbox, StackLayout } from '@leagueplatform/genesis-core';
import { useTranslate } from '@web-config-app/core-react';
import {
  ArrayPrimitiveGroup,
  EntityFormArrayControl,
} from '@web-config-app/entity-form';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
} from '@web-config-app/entity-form';
import type { AnnotatedJsonSchema } from '@web-config-app/core';

type CheckboxOption = {
  label: string;
  value: string;
};

const getArraySchemaAtPath = (
  rootSchema: AnnotatedJsonSchema,
  path: string,
): AnnotatedJsonSchema | undefined => {
  const segments = path.split('.').filter(Boolean);
  let currentSchema: AnnotatedJsonSchema | undefined = rootSchema;

  segments.forEach((segment) => {
    if (!currentSchema) return;

    if (currentSchema.type === 'array') {
      currentSchema = currentSchema.items as AnnotatedJsonSchema | undefined;
    }

    if (/^\d+$/.test(segment)) return;

    currentSchema = currentSchema?.properties?.[segment] as
      | AnnotatedJsonSchema
      | undefined;
  });

  return currentSchema?.type === 'array' ? currentSchema : undefined;
};

const getCheckboxOptions = (
  arraySchema: AnnotatedJsonSchema | undefined,
  translate: ReturnType<typeof useTranslate>,
): CheckboxOption[] => {
  const itemSchema = arraySchema?.items as AnnotatedJsonSchema | undefined;
  const enumValues = itemSchema?.enum;
  const enumLabels = itemSchema?.['x-entity-presentation']?.enumLabels;

  if (!Array.isArray(enumValues)) return [];

  return enumValues
    .filter((value): value is string => typeof value === 'string')
    .map((value) => {
      const translatedLabel =
        typeof enumLabels?.[value] === 'string'
          ? translate(enumLabels[value])
          : undefined;

      return {
        value,
        label: translatedLabel ?? value,
      };
    });
};

const getNextSelectedValues = ({
  checked,
  currentValue,
  optionValue,
  orderedOptionValues,
}: {
  checked: boolean;
  currentValue: string[];
  optionValue: string;
  orderedOptionValues: string[];
}) => {
  const selectedValues = new Set(currentValue);

  if (checked) {
    selectedValues.add(optionValue);
  } else {
    selectedValues.delete(optionValue);
  }

  return orderedOptionValues.filter((value) => selectedValues.has(value));
};

const CheckboxGroupContents = ({
  data,
  path,
  rootSchema,
  label,
  hint,
  banner,
  hideLabel,
  errors,
  inputStatus,
  statusMessage,
  enabled,
  handleUpdateArrayItem,
}: EntityFormArrayControlProps) => {
  const translate = useTranslate();
  const arraySchema = getArraySchemaAtPath(
    rootSchema as AnnotatedJsonSchema,
    path,
  );
  const options = getCheckboxOptions(arraySchema, translate);
  const selectedValues = Array.isArray(data) ? data : [];
  const orderedOptionValues = options.map(({ value }) => value);

  return (
    <ArrayPrimitiveGroup
      hint={hint}
      label={label}
      banner={banner}
      hideLabel={hideLabel}
      errors={errors}
      statusMessage={statusMessage}
      inputStatus={inputStatus}
    >
      <StackLayout spacing="$half">
        {options.map(({ label: optionLabel, value }) => {
          const id = `${path}-${value}`.replace(/[^a-zA-Z0-9_-]/g, '-');

          return (
            <Checkbox
              key={value}
              name={id}
              id={id}
              label={optionLabel}
              checked={selectedValues.includes(value)}
              aria-readonly={!enabled}
              onChange={
                enabled
                  ? () => {
                      handleUpdateArrayItem(
                        path,
                        getNextSelectedValues({
                          checked: !selectedValues.includes(value),
                          currentValue: selectedValues,
                          optionValue: value,
                          orderedOptionValues,
                        }),
                      );
                    }
                  : () => false
              }
              css={{
                ...(!enabled && {
                  '.GDS-checkbox-label': {
                    'pointer-events': 'none',
                  },
                }),
              }}
            />
          );
        })}
      </StackLayout>
    </ArrayPrimitiveGroup>
  );
};

export const CheckboxGroupControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={CheckboxGroupContents} />
);
